import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';

const GOLD = '#c8a04e';
const W = (a) => `rgba(255,255,255,${a})`;
const GA = (a) => `rgba(200,160,78,${a})`;

const ROW_A = [
  { name: 'xBlaze',    role: 'Lifetime',  stars: 5, text: 'Auto-detection is flawless. Switches profiles before I even finish looting.' },
  { name: 'kr4tos',    role: '30 Days',   stars: 5, text: 'Ghost Mode is a lifesaver when streaming. Never had a close call since.' },
  { name: 'nv_reaper', role: 'Lifetime',  stars: 5, text: 'Setup took maybe 4 minutes. Was already in a server destroying people shortly after.' },
  { name: 'drift404',  role: '30 Days',   stars: 4, text: 'Had a small config issue on first launch. Support sorted it within minutes. Works perfectly now.' },
  { name: 'pulze',     role: '7 Days',    stars: 5, text: 'Cleanest recoil I have ever used. Tried a lot of alternatives and nothing compares.' },
  { name: 'tavrox',    role: 'Lifetime',  stars: 5, text: 'Scope support across all zoom levels is seriously impressive. Irons to 16x just works.' },
  { name: 'krel99',    role: 'Lifetime',  stars: 4, text: 'Was skeptical at first but after a week I went straight for the lifetime. Worth every penny.' },
];
const ROW_B = [
  { name: 'mxlody',   role: 'Lifetime',  stars: 5, text: 'Been using it for over a year. Updates always drop fast and nothing ever breaks.' },
  { name: 'snxpez',   role: '30 Days',   stars: 5, text: 'Patches keep up with the game faster than I expected. Team clearly knows what they are doing.' },
  { name: 'coldrift', role: 'Lifetime',  stars: 5, text: 'Zero FPS drop. Game runs exactly the same as without it. Was not expecting that at all.' },
  { name: 'v0rtex',   role: '7 Days',    stars: 4, text: 'Hit a weird bug after a game update. Opened a ticket and it was fixed same day. Respect.' },
  { name: 'rykzed',   role: '30 Days',   stars: 5, text: 'Eight months straight and I have not had a single issue worth complaining about.' },
  { name: 'phntm',    role: 'Lifetime',  stars: 5, text: 'Runs completely in the background. Zero interference with the game, zero detections.' },
  { name: 'solark',   role: '30 Days',   stars: 4, text: 'Support answered faster than I expected. Issue got resolved and I renewed the same day.' },
];

function TestimonialCard({ name, role, stars, text }) {
  return (
    <div style={{
      padding: '22px', borderRadius: 12,
      height: '100%', boxSizing: 'border-box',
      border: '1px solid rgba(255,255,255,0.08)',
      background: 'radial-gradient(ellipse 130% 90% at 0% 0%, rgba(200,160,78,0.07) 0%, rgba(255,255,255,0.03) 55%, rgba(255,255,255,0.02) 100%)',
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
    }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 14 }}>
        {[...Array(5)].map((_, i) => (
          <svg key={i} width="12" height="12" viewBox="0 0 16 16" fill={i < stars ? GOLD : 'none'} stroke={i < stars ? 'none' : GA(0.3)} strokeWidth="1">
            <path d="M8 1l1.9 4 4.4.6-3.2 3.1.8 4.4L8 11.1l-3.9 2 .8-4.4L1.7 5.6l4.4-.6z"/>
          </svg>
        ))}
      </div>
      <p style={{ fontSize: 13, color: W(0.6), lineHeight: 1.7, margin: '0 0 18px' }}>"{text}"</p>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: W(0.82) }}>{name}</div>
        <div style={{ fontSize: 11, color: role === 'Lifetime' ? GA(0.7) : W(0.28), marginTop: 2 }}>{role}</div>
      </div>
    </div>
  );
}

function MarqueeRow({ cards, duration, reverse }) {
  const doubled = [...cards, ...cards];
  return (
    <div style={{ overflow: 'hidden', width: '100%' }}>
      <style>{`
        @keyframes scrollLeft  { 0% { transform: translateX(0); }    100% { transform: translateX(-50%); } }
        @keyframes scrollRight { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
      `}</style>
      <div style={{ display: 'flex', gap: 14, width: 'max-content', animation: `${reverse ? 'scrollRight' : 'scrollLeft'} ${duration}s linear infinite` }}>
        {doubled.map((t, i) => (
          <div key={i} style={{ width: 290, flexShrink: 0 }}>
            <TestimonialCard {...t} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    repeatPassword: '',
    agreeTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Username validation: 3-20 chars, alphanumeric + underscore only
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      setError("Username must be 3-20 characters, letters, numbers, and underscores only");
      setLoading(false);
      return;
    }

    // Password strength: min 8 chars, at least one letter and one number
    if (formData.password.length < 8 || !/[a-zA-Z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      setError("Password must be at least 8 characters with at least one letter and one number");
      setLoading(false);
      return;
    }

    if (formData.password !== formData.repeatPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (!formData.agreeTerms) {
      setError("You must agree to the Terms of Service");
      setLoading(false);
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            username: formData.username
          }
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', overflow: 'hidden' }}>

      {/* ── LEFT: Form ── */}
      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 56px', overflow: 'hidden', position: 'relative' }}>


        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          style={{ width: '100%', maxWidth: 400 }}
        >
          {success ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: 64, height: 64, margin: '0 auto 24px', borderRadius: 16, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>Registration Complete</h2>
              <p style={{ fontSize: 13.5, color: W(0.38), margin: '0 0 24px', lineHeight: 1.6 }}>Check your email to verify your account before signing in.</p>
              <div style={{ padding: '12px 16px', background: GA(0.06), border: `1px solid ${GA(0.12)}`, borderRadius: 12, marginBottom: 24 }}>
                <p style={{ fontSize: 12, color: GA(0.8), margin: 0 }}>Verification link sent to <strong style={{ color: GOLD }}>{formData.email}</strong></p>
              </div>
              <Link to={plan ? `/login?plan=${plan}` : '/login'} style={{ display: 'block', width: '100%', padding: '13px', borderRadius: 10, background: GA(0.08), border: `1px solid ${GA(0.28)}`, color: GOLD, fontWeight: 700, fontSize: 13.5, textDecoration: 'none', textAlign: 'center', letterSpacing: '0.04em', boxSizing: 'border-box' }}>
                {plan ? 'Sign In & Complete Purchase' : 'Go to Login'}
              </Link>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 32 }}>
                <h1 style={{ fontSize: 26, fontWeight: 700, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Create Account</h1>
                <p style={{ fontSize: 13.5, color: W(0.35), margin: 0 }}>Join thousands of elite players today</p>
              </div>

              {error && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  style={{ marginBottom: 20, padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, fontSize: 12.5, color: 'rgba(239,68,68,0.9)', textAlign: 'center' }}>
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { label: 'Username', key: 'username', type: 'text', placeholder: 'Choose a username' },
                  { label: 'Email Address', key: 'email', type: 'email', placeholder: 'name@example.com' },
                  { label: 'Password', key: 'password', type: 'password', placeholder: '••••••••' },
                  { label: 'Repeat Password', key: 'repeatPassword', type: 'password', placeholder: '••••••••' },
                ].map(({ label, key, type, placeholder }) => (
                  <div key={key}>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: W(0.3), letterSpacing: '0.1em', marginBottom: 7, textTransform: 'uppercase' }}>{label}</label>
                    <input
                      type={type} required placeholder={placeholder}
                      value={formData[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                      style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '11px 16px', color: '#fff', fontSize: 13.5, outline: 'none', transition: 'border-color .15s' }}
                      onFocus={e => e.target.style.borderColor = GA(0.4)}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                    />
                  </div>
                ))}

                {/* Terms */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, agreeTerms: !formData.agreeTerms })}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10, paddingTop: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                >
                  <div
                    style={{
                      flexShrink: 0, marginTop: 2, width: 16, height: 16, borderRadius: 4,
                      background: formData.agreeTerms ? 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.38) 0%, rgba(200,160,78,0.18) 55%, rgba(200,160,78,0.08) 100%)' : 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                      border: formData.agreeTerms ? '1px solid rgba(200,160,78,0.45)' : '1px solid rgba(255,255,255,0.12)',
                      boxShadow: formData.agreeTerms ? '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(200,160,78,0.3)' : '0 2px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.07)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all .15s',
                    }}
                  >
                    {formData.agreeTerms && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4.5 7.5L8 2.5" stroke="#c8a04e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: W(0.35), lineHeight: 1.6 }}>
                    I agree to the <Link to="/terms" onClick={e => e.stopPropagation()} style={{ color: GOLD, textDecoration: 'none', transition: 'opacity .15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.65'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>Terms of Service</Link> and <Link to="/privacy" onClick={e => e.stopPropagation()} style={{ color: GOLD, textDecoration: 'none', transition: 'opacity .15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.65'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>Privacy Policy</Link>.
                  </span>
                </button>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  disabled={loading}
                  style={{ marginTop: 4, padding: '13px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)', border: `1px solid ${GA(0.28)}`, boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(200,160,78,0.1)', color: GOLD, fontWeight: 700, fontSize: 13.5, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, letterSpacing: '0.01em', transition: 'background .2s, border-color .2s' }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.18) 0%, rgba(200,160,78,0.08) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.45)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = GA(0.28); }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </motion.button>
              </form>

              <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: W(0.35), margin: 0 }}>
                  Already have an account?{' '}
                  <Link to={plan ? `/login?plan=${plan}` : '/login'} style={{ color: GOLD, fontWeight: 600, textDecoration: 'none', transition: 'opacity .15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.65'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>Sign In</Link>
                </p>
              </div>
            </>
          )}
        </motion.div>

        {/* Back to home */}
        <Link to="/" style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', fontSize: 12, color: W(0.25), textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, transition: 'color .15s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.color = W(0.6)}
          onMouseLeave={e => e.currentTarget.style.color = W(0.25)}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Back to Homepage
        </Link>
      </div>

      {/* ── DIVIDER ── */}
      <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

      {/* ── RIGHT: Testimonials ── */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16 }}>

        {/* Grid lines — same as Features section */}
        <style>{`
          @keyframes regDrop {
            0%   { top: -20%; }
            100% { top: 110%; }
          }
        `}</style>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          {[
            { left: '10%',   delay: '0s'   },
            { left: '25%',   delay: '2.2s' },
            { left: '42%',   delay: '4.5s' },
            { left: '58%',   delay: '0.8s' },
            { left: '75%',   delay: '3.1s' },
            { left: '90%',   delay: '1.6s' },
          ].map(({ left, delay }, i) => (
            <div key={i} style={{ position: 'absolute', width: 1, height: '100%', top: 0, left, background: 'rgba(255,255,255,0.055)', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', width: '100%', height: '15vh', left: 0, top: '-20%', background: 'linear-gradient(to bottom, rgba(200,160,78,0) 0%, rgba(200,160,78,0.65) 75%, rgba(200,160,78,0.85) 100%)', animation: `regDrop 7s ${delay} infinite cubic-bezier(0.4,0.26,0,0.97)`, animationFillMode: 'backwards' }} />
            </div>
          ))}
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: `${Math.round((i/5)*100)}%`, height: 1, background: 'rgba(255,255,255,0.04)' }} />
          ))}
        </div>

        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.04,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }} />

        {/* Edge fades */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to right, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to left, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />

        {/* Label */}
        <p style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: W(0.15), letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 8px', position: 'relative', zIndex: 3 }}>What players are saying</p>

        {/* Row 1 — left */}
        <MarqueeRow cards={ROW_A} duration={28} reverse={false} />
        {/* Row 2 — right */}
        <MarqueeRow cards={ROW_B} duration={32} reverse={true} />
      </div>
    </div>
  );
}
